import React, { useState, useEffect } from 'react';
import { useParams, Link, Navigate } from 'react-router-dom';
import { Calendar, User, ArrowLeft, ArrowRight, Loader2 } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ComparisonTable = () => {
  const tableData = [
    {
      process: "Litigation",
      cost: "Highest (two lawyers, court fees)",
      timeFrame: "Often many months to years",
      privacy: "Low-moderate (public court files)",
      whoDecides: "Judge, if you don't settle",
      bestFit: "Very high conflict, urgent safety concerns"
    },
    {
      process: "Mediation",
      cost: "Lower-moderate (shared mediator)",
      timeFrame: "Often a few months, flexible",
      privacy: "Higher (private sessions)",
      whoDecides: "You and your spouse together",
      bestFit: "Willing participants, moderate conflict"
    },
    {
      process: "Collaborative Divorce",
      cost: "Moderate-high (two lawyers + team)",
      timeFrame: "Several months to a year+",
      privacy: "Higher (team meetings)",
      whoDecides: "You and your spouse together",
      bestFit: "Higher conflict but settlement-minded"
    },
    {
      process: "DIY / Unbundled",
      cost: "Lowest cash cost",
      timeFrame: "Varies; can be quick or stalled",
      privacy: "Moderate (still requires filings)",
      whoDecides: "You and your spouse",
      bestFit: "Low conflict, simple cases"
    }
  ];

  return (
    <div className="my-10 overflow-x-auto">
      <table className="w-full border-collapse rounded-lg overflow-hidden shadow-lg">
        <thead>
          <tr style={{ backgroundColor: '#5E7F8A' }}>
            <th className="px-4 py-4 text-left text-white font-semibold text-sm">Process</th>
            <th className="px-4 py-4 text-left text-white font-semibold text-sm">Cost</th>
            <th className="px-4 py-4 text-left text-white font-semibold text-sm">Time Frame</th>
            <th className="px-4 py-4 text-left text-white font-semibold text-sm">Privacy</th>
            <th className="px-4 py-4 text-left text-white font-semibold text-sm">Who Decides?</th>
            <th className="px-4 py-4 text-left text-white font-semibold text-sm">Best Fit</th>
          </tr>
        </thead>
        <tbody>
          {tableData.map((row, index) => (
            <tr 
              key={index} 
              className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
              style={{ borderBottom: '1px solid #e5e7eb' }}
            >
              <td className="px-4 py-4 font-semibold text-sm" style={{ color: '#5E7F8A' }}>{row.process}</td>
              <td className="px-4 py-4 text-sm" style={{ color: '#5E7F8A' }}>{row.cost}</td>
              <td className="px-4 py-4 text-sm" style={{ color: '#5E7F8A' }}>{row.timeFrame}</td>
              <td className="px-4 py-4 text-sm" style={{ color: '#5E7F8A' }}>{row.privacy}</td>
              <td className="px-4 py-4 text-sm" style={{ color: '#5E7F8A' }}>{row.whoDecides}</td>
              <td className="px-4 py-4 text-sm" style={{ color: '#5E7F8A' }}>{row.bestFit}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <p className="text-sm mt-4 italic" style={{ color: '#5E7F8A' }}>
        This table is a general guide, not a guarantee. The specifics of your situation, your local court, and the professionals you work with all matter.
      </p>
    </div>
  );
};

// Related Posts Component
const RelatedPosts = ({ currentSlug, allPosts }) => {
  // Get other posts excluding current one, sorted by date (most recent first)
  const relatedPosts = allPosts
    .filter(post => post.slug !== currentSlug)
    .sort((a, b) => new Date(b.date) - new Date(a.date))
    .slice(0, 3);

  if (relatedPosts.length === 0) return null;

  return (
    <div className="mt-16 pt-12 border-t border-gray-200">
      <h2 className="text-2xl font-bold mb-8" style={{ color: '#5E7F8A' }}>
        Related Articles
      </h2>
      <div className="grid md:grid-cols-3 gap-6">
        {relatedPosts.map((post) => (
          <Link
            key={post.slug}
            to={`/blog/${post.slug}`}
            className="group block bg-gray-50 rounded-lg overflow-hidden hover:shadow-lg transition-shadow"
            data-testid={`related-post-${post.slug}`}
          >
            <div className="h-40 overflow-hidden">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
              />
            </div>
            <div className="p-4">
              <p className="text-xs mb-2" style={{ color: '#9BB2A2' }}>
                {new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
              </p>
              <h3 
                className="font-semibold text-sm leading-snug group-hover:underline line-clamp-2"
                style={{ color: '#5E7F8A' }}
              >
                {post.title}
              </h3>
              <div 
                className="flex items-center mt-3 text-sm font-medium"
                style={{ color: '#9BB2A2' }}
              >
                Read more <ArrowRight size={14} className="ml-1" />
              </div>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

const BlogPost = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        // Fetch the specific blog post
        const postResponse = await fetch(`${API_URL}/api/blogs/${slug}`);
        if (!postResponse.ok) {
          if (postResponse.status === 404) {
            setNotFound(true);
            return;
          }
          throw new Error('Failed to fetch blog post');
        }
        const postData = await postResponse.json();
        setPost(postData);

        // Fetch all posts for related articles
        const allResponse = await fetch(`${API_URL}/api/blogs`);
        if (allResponse.ok) {
          const allData = await allResponse.json();
          setAllPosts(allData.blogs || []);
        }
      } catch (err) {
        console.error('Error fetching blog:', err);
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-white pt-32 pb-20 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin" style={{ color: '#5E7F8A' }} />
      </div>
    );
  }

  if (notFound || !post) {
    return <Navigate to="/blog" replace />;
  }

  return (
    <div className="min-h-screen bg-white pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Link 
          to="/blog"
          className="inline-flex items-center mb-8 font-semibold transition-opacity hover:opacity-80"
          style={{ color: '#5E7F8A' }}
          data-testid="back-to-blog"
        >
          <ArrowLeft size={20} className="mr-2" />
          Back to Blog
        </Link>

        {/* Hero Image */}
        <div className="mb-8">
          <img 
            src={post.image} 
            alt={post.title}
            className="w-full h-96 object-cover rounded-lg shadow-lg"
          />
        </div>

        {/* Post Meta */}
        <div className="flex items-center space-x-4 mb-6 text-sm" style={{ color: '#5E7F8A' }}>
          <div className="flex items-center space-x-2">
            <Calendar size={18} />
            <span>{new Date(post.date).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</span>
          </div>
          <div className="flex items-center space-x-2">
            <User size={18} />
            <span>{post.author}</span>
          </div>
        </div>

        {/* Title */}
        <h1 className="text-4xl md:text-5xl font-bold mb-8 leading-tight" style={{ color: '#5E7F8A' }}>
          {post.title}
        </h1>

        {/* Divider */}
        <div className="w-24 h-1 mb-8" style={{ backgroundColor: '#9BB2A2' }}></div>

        {/* Content */}
        <div 
          className="prose prose-lg max-w-none blog-content"
          style={{ color: '#5E7F8A' }}
        >
          {post.content && post.content.split('\n\n').map((paragraph, index) => {
            // Handle comparison table placeholder
            if (paragraph.trim() === '[COMPARISON_TABLE]') {
              return <ComparisonTable key={index} />;
            }
            // Handle headings
            if (paragraph.startsWith('## ')) {
              return (
                <h2 key={index} className="text-3xl font-bold mt-12 mb-6" style={{ color: '#5E7F8A' }}>
                  {paragraph.replace('## ', '')}
                </h2>
              );
            }
            if (paragraph.startsWith('### ')) {
              return (
                <h3 key={index} className="text-2xl font-semibold mt-8 mb-4" style={{ color: '#5E7F8A' }}>
                  {paragraph.replace('### ', '')}
                </h3>
              );
            }
            // Handle lists
            if (paragraph.startsWith('- ')) {
              const items = paragraph.split('\n');
              return (
                <ul key={index} className="list-disc pl-6 space-y-2 my-6">
                  {items.map((item, i) => (
                    <li key={i} className="text-lg leading-relaxed">
                      {item.replace('- ', '')}
                    </li>
                  ))}
                </ul>
              );
            }
            // Handle paragraphs with inline bold (e.g., "**Bold text** regular text")
            if (paragraph.includes('**')) {
              const parts = paragraph.split(/(\*\*.*?\*\*)/g);
              return (
                <p key={index} className="text-lg leading-relaxed my-6">
                  {parts.map((part, i) => {
                    if (part.startsWith('**') && part.endsWith('**')) {
                      return <strong key={i}>{part.replace(/\*\*/g, '')}</strong>;
                    }
                    return part;
                  })}
                </p>
              );
            }
            // Regular paragraphs
            return (
              <p key={index} className="text-lg leading-relaxed my-6">
                {paragraph}
              </p>
            );
          })}
        </div>

        {/* CTA Section */}
        <div className="mt-16 bg-gray-50 rounded-lg p-10 text-center">
          <h2 className="text-2xl font-bold mb-4" style={{ color: '#5E7F8A' }}>
            Ready to Take the Next Step?
          </h2>
          <p className="text-lg mb-6" style={{ color: '#5E7F8A' }}>
            Schedule a free consultation to discuss how mediation can help your family.
          </p>
          <Link 
            to="/contact"
            className="inline-block px-8 py-4 rounded-md text-white font-semibold transition-all hover:shadow-lg"
            style={{ backgroundColor: '#9BB2A2' }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#8FA596'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#9BB2A2'}
            data-testid="contact-cta"
          >
            Contact Us
          </Link>
        </div>

        {/* Related Posts Section */}
        <RelatedPosts currentSlug={slug} allPosts={allPosts} />
      </div>
    </div>
  );
};

export default BlogPost;
